import {
    buttonBaseClasses,
    buttonClasses,
    ButtonProps,
    ComponentsOverrides,
    ComponentsProps,
    ComponentsVariants,
    svgIconClasses,
    Theme,
} from '@mui/material';
import { StyleProps } from './utils/dynamicStyle';
import { generateFocusRingStyles } from './utils/focus';

const focusRingStyles = generateFocusRingStyles(buttonBaseClasses.focusVisible);

type StyleHelperFunctionProps = StyleProps<ButtonProps>;

function sizeStyles(props: StyleHelperFunctionProps): { padding: string } {
    const containedAndOutlinedSizeStyle = {
        small: {
            padding: props.theme.spacing(props.theme.tokens.spacing.xxs, props.theme.tokens.spacing.sm),
        },
        medium: {
            padding: props.theme.spacing(props.theme.tokens.spacing.xs, props.theme.tokens.spacing.xl),
        },
    } as const;
    if (props.ownerState.variant && props.ownerState.size) {
        const styleMap = {
            contained: containedAndOutlinedSizeStyle,
            outlined: containedAndOutlinedSizeStyle,
            text: {
                small: {
                    padding: props.theme.spacing(props.theme.tokens.spacing.xxs, props.theme.tokens.spacing.xs),
                },
                medium: {
                    padding: props.theme.spacing(props.theme.tokens.spacing.xs, props.theme.tokens.spacing.md),
                },
            },
        } as const;

        return styleMap[props.ownerState.variant][props.ownerState.size];
    }
    return containedAndOutlinedSizeStyle.medium;
}

function colorStyles(props: StyleHelperFunctionProps) {
    if (props.ownerState.variant && props.ownerState && props.ownerState.color !== 'inherit') {
        const palette =
            props.ownerState?.color && props.theme.palette[props.ownerState?.color]
                ? props.theme.palette[props.ownerState?.color]
                : props.theme.palette.primary;
        const styleMap = {
            contained: {
                [`&.${buttonClasses.focusVisible}`]: {
                    backgroundColor: palette.dark,
                },
            },
            outlined: {
                borderColor: palette.main,
                [`&.${buttonClasses.focusVisible},:hover,:active`]: {
                    backgroundColor: palette.light,
                    color: palette.dark,
                    borderColor: palette.dark,
                },
            },
            text: {
                [`&.${buttonClasses.focusVisible},:hover,:active`]: {
                    backgroundColor: props.theme.palette.secondary.light,
                },
            },
        };

        return styleMap[props.ownerState.variant];
    }
    return {};
}

function borderStyles(props: StyleHelperFunctionProps) {
    const shared = {
        borderWidth: props.theme.tokens.borderWidth.md,
        borderStyle: 'solid',
    };

    if (props.ownerState.variant && props.ownerState && props.ownerState.color !== 'inherit') {
        const palette =
            props.ownerState?.color && props.theme.palette[props.ownerState?.color]
                ? props.theme.palette[props.ownerState?.color]
                : props.theme.palette.primary;

        const styleMap = {
            contained: {
                borderColor: palette.main,
                [`&.${buttonClasses.focusVisible},:hover,:active`]: {
                    borderColor: palette.dark,
                },
                [`&.${buttonBaseClasses.disabled}`]: {
                    borderColor: 'transparent',
                },
            },
            outlined: {
                [`&.${buttonBaseClasses.disabled}`]: {
                    // needed because of a specificity issue
                    borderWidth: props.theme.tokens.borderWidth.md,
                },
            },
            text: { borderColor: 'transparent' },
            shared: {
                borderWidth: props.theme.tokens.borderWidth.md,
                borderStyle: 'solid',
            },
        };
        const variantStyles = styleMap[props.ownerState.variant] || {};
        return { ...shared, ...variantStyles };
    }
    return shared;
}

export const MuiButtonBase: {
    defaultProps?: ComponentsProps['MuiButtonBase'];
    styleOverrides?: ComponentsOverrides<Theme>['MuiButtonBase'];
    variants?: ComponentsVariants['MuiButtonBase'];
} = {
    defaultProps: {
        disableRipple: true,
    },
    styleOverrides: {
        root: [focusRingStyles],
    },
};

export const MuiButton: {
    defaultProps?: ComponentsProps['MuiButton'];
    styleOverrides?: ComponentsOverrides<Theme>['MuiButton'];
    variants?: ComponentsVariants['MuiButton'];
} = {
    defaultProps: {
        variant: 'contained',
        size: 'medium',
        color: 'primary',
        disableElevation: true,
    },
    styleOverrides: {
        iconSizeMedium: {
            [`& .${svgIconClasses.root}`]: {
                color: 'inherit',
                fontSize: '1.25rem',
            },
        },
        iconSizeSmall: {
            [`& .${svgIconClasses.root}`]: {
                color: 'inherit',
                fontSize: '1.125rem',
            },
        },
        root: (props) => [
            sizeStyles(props),
            colorStyles(props),
            borderStyles(props),
            {
                borderRadius: props.theme.tokens.borderRadius.round,
                boxShadow: 'none',
                [`&.${buttonClasses.focusVisible},:hover,:active`]: {
                    boxShadow: 'none',
                    borderWidth: props.theme.tokens.borderWidth.md,
                },
            },
        ],
    },
};
